// /pages/HR/utils/hrConstants.js

/**
 * HR Dashboard Constants
 * Centralized constants for HR module
 */

// Location constants
export const HR_LOCATIONS = [
  { code: 'FL', name: 'Florida', icon: '🌴', timezone: 'America/New_York' },
  { code: 'MA', name: 'Massachusetts', icon: '✨', timezone: 'America/New_York' },
  { code: 'ME', name: 'Maine', icon: '🦞', timezone: 'America/New_York' },
  { code: 'NH', name: 'New Hampshire', icon: '🏔️', timezone: 'America/New_York' },
  { code: 'NY', name: 'New York', icon: '🗽', timezone: 'America/New_York' },
  { code: 'RI', name: 'Rhode Island', icon: '⚓', timezone: 'America/New_York' },
  { code: 'XX', name: 'Other', icon: '📍', timezone: 'America/New_York' },
];

// Category constants
export const HR_CATEGORIES = [
  { 
    id: 'employee_leave', 
    name: 'Employee Leave', 
    group: 'Time Off',
    color: '#5B9BD5',
    icon: '🏖️',
    priority: 1 
  },
  { 
    id: 'payroll', 
    name: 'Payroll Request', 
    group: 'Compensation',
    color: '#2E5090',
    icon: '💰',
    priority: 1 
  },
  { 
    id: 'benefits', 
    name: 'Benefits Request', 
    group: 'Benefits',
    color: '#70AD47',
    icon: '🏥',
    priority: 2 
  },
  { 
    id: 'people_plan', 
    name: 'People Plan', 
    group: 'Employee Data',
    color: '#FFC000',
    icon: '📊',
    priority: 2 
  },
  { 
    id: 'employee_info', 
    name: 'Employee Information Change', 
    group: 'Employee Data',
    color: '#4472C4',
    icon: '📝',
    priority: 2 
  },
  { 
    id: 'verification', 
    name: 'Employment Verification', 
    group: 'Employee Data',
    color: '#5B9BD5',
    icon: '✅',
    priority: 3 
  },
  { 
    id: 'correction', 
    name: 'Minor Documentation Correction', 
    group: 'Employee Data',
    color: '#70AD47',
    icon: '📋',
    priority: 3 
  },
  { 
    id: 'complaint', 
    name: 'HR Complaint/Concern', 
    group: 'Employee Relations',
    color: '#ED7D31',
    icon: '⚠️',
    priority: 1 
  },
  { 
    id: 'termination', 
    name: 'Termination', 
    group: 'Offboarding',
    color: '#A5A5A5',
    icon: '🚪',
    priority: 1 
  },
  { 
    id: 'writing_assessment', 
    name: 'Writing Assessment', 
    group: 'Performance',
    color: '#FFC000',
    icon: '✍️',
    priority: 3 
  },
  { 
    id: 'TA', 
    name: 'TA', 
    group: 'Talent Acquisition',
    color: '#4472C4',
    icon: '🎯',
    priority: 2 
  },
];

// Market emoji mapping
export const MARKET_EMOJI_MAP = {
  'FL': '🌴',
  'MA': '✨',
  'ME': '🦞',
  'NH': '🏔️',
  'NY': '🗽',
  'RI': '⚓',
  'XX': '📍'
};

// Status constants - matching database values
export const HR_REQUEST_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  PENDING: 'pending',
  RESOLVED: 'resolved',
  CANCELLED: 'cancelled',
};

// Status display configuration
export const STATUS_CONFIG = {
  [HR_REQUEST_STATUS.NEW]: {
    label: 'New',
    color: '#8B5CF6',
    bgColor: '#EDE9FE',
    icon: '🆕',
  },
  [HR_REQUEST_STATUS.IN_PROGRESS]: {
    label: 'In Progress',
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    icon: '🔄',
  },
  [HR_REQUEST_STATUS.PENDING]: {
    label: 'Pending',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    icon: '⏳',
  },
  [HR_REQUEST_STATUS.RESOLVED]: {
    label: 'Resolved',
    color: '#10B981',
    bgColor: '#D1FAE5',
    icon: '✅',
  },
  [HR_REQUEST_STATUS.CANCELLED]: {
    label: 'Cancelled',
    color: '#6B7280',
    bgColor: '#F3F4F6',
    icon: '❌',
  },
};

// Chart color schemes - UPDATED TO MATCH YOUR IMAGE
export const CHART_COLORS = {
  // Category chart - gradient from light to dark blue
  categoryGradient: [
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#C5E4FF',  // Very light blue
    '#70AD47',  // Light green for TA
    '#5B9BD5',  // Medium blue
    '#5B9BD5',  // Medium blue
    '#2E5090',  // Dark blue
    '#203864',  // Very dark blue
  ],
  
  // Generalist chart - all dark blue
  generalistBars: '#203864',  // Dark navy blue
  
  // Weekly combo chart colors
  weeklyBarColor: '#FFC000',  // Yellow/gold
  weeklyBarColorDark: '#203864',  // Dark blue for specific weeks
  weeklyLineColor: '#4B5563',  // Dark grey for line
  
  // Pie chart colors - matching the market distribution
  pieColors: [
    '#5B9BD5',  // NH - Blue
    '#4472C4',  // MA - Dark blue/purple
    '#FFC000',  // ME - Yellow
    '#E15759',  // RI - Red
    '#70AD47',  // FL - Green
    '#70C0E8',  // NY - Light blue
    '#ED7D31',  // XX - Orange
  ],
  
  // Legacy gradient (keeping for backwards compatibility)
  gradient: [
    'rgba(91, 155, 213, 0.8)',   // Light blue
    'rgba(46, 80, 144, 0.8)',    // Dark blue
    'rgba(91, 155, 213, 0.6)',
    'rgba(46, 80, 144, 0.6)',
    'rgba(91, 155, 213, 0.4)',
    'rgba(46, 80, 144, 0.4)',
  ],
  
  // Legacy categorical (keeping for backwards compatibility)
  categorical: [
    '#5B9BD5', '#4472C4', '#70AD47', '#FFC000', 
    '#ED7D31', '#A5A5A5', '#E15759', '#70C0E8',
    '#F1CE63', '#6C8EBF', '#82B366', '#9F9F9F',
  ],
};

// Date range presets
export const DATE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last7days',
  LAST_30_DAYS: 'last30days',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  THIS_QUARTER: 'thisQuarter',
  LAST_QUARTER: 'lastQuarter',
  YEAR_TO_DATE: 'ytd',
  LAST_YEAR: 'lastYear',
  ALL_TIME: 'allTime',
};

// Export formats
export const EXPORT_FORMATS = {
  PDF: 'pdf',
  EXCEL: 'excel',
  CSV: 'csv',
  JSON: 'json',
  PNG: 'png',
};

// Refresh intervals (in milliseconds)
export const REFRESH_INTERVALS = {
  REAL_TIME: 5000,      // 5 seconds
  FREQUENT: 30000,      // 30 seconds
  NORMAL: 60000,        // 1 minute
  MODERATE: 300000,     // 5 minutes
  SLOW: 900000,         // 15 minutes
};

// Permission levels
export const HR_PERMISSIONS = {
  VIEW_DASHBOARD: 'hr.dashboard.view',
  VIEW_REQUESTS: 'hr.requests.view',
  CREATE_REQUEST: 'hr.requests.create',
  EDIT_REQUEST: 'hr.requests.edit',
  DELETE_REQUEST: 'hr.requests.delete',
  APPROVE_REQUEST: 'hr.requests.approve',
  EXPORT_DATA: 'hr.data.export',
  VIEW_REPORTS: 'hr.reports.view',
  MANAGE_SETTINGS: 'hr.settings.manage',
};

// Notification types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Sort options
export const SORT_OPTIONS = {
  DATE_ASC: { field: 'date', direction: 'asc', label: 'Date (Oldest First)' },
  DATE_DESC: { field: 'date', direction: 'desc', label: 'Date (Newest First)' },
  STATUS_ASC: { field: 'status', direction: 'asc', label: 'Status (A-Z)' },
  STATUS_DESC: { field: 'status', direction: 'desc', label: 'Status (Z-A)' },
  PRIORITY_HIGH: { field: 'priority', direction: 'asc', label: 'Priority (High to Low)' },
  PRIORITY_LOW: { field: 'priority', direction: 'desc', label: 'Priority (Low to High)' },
};

// Validation rules
export const VALIDATION_RULES = {
  REQUEST_TITLE_MIN: 3,
  REQUEST_TITLE_MAX: 100,
  REQUEST_DESCRIPTION_MIN: 10,
  REQUEST_DESCRIPTION_MAX: 5000,
  ATTACHMENT_MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg'],
};

// API endpoints
export const API_ENDPOINTS = {
  DASHBOARD: '/hr/dashboard',
  KPIS: '/hr/kpis',
  REQUESTS: '/hr/requests',
  CATEGORIES: '/hr/categories',
  LOCATIONS: '/hr/locations',
  WEEKLY: '/hr/weekly',
  GENERALISTS: '/hr/generalists',
  MARKET_DISTRIBUTION: '/hr/market-distribution',
  FILTER_OPTIONS: '/hr/filter-options',
  EXPORT: '/hr/export',
  UPLOAD: '/hr/upload',
};